/**
 * Resource functions — one async call per logical endpoint. Today they return
 * the existing mock fixtures (USE_MOCKS); the false branch hits the real API at
 * the same shape. Shapes here are the CURRENT fixture shapes so a page can move
 * from `import { const }` to `useX()` with no render change; the separate
 * contract-shape migration (minor-unit money, ISO dates, renamed fields per
 * src/contracts) is tracked in docs/API_CONTRACT.md.
 */
import { useQuery } from '@tanstack/react-query';
import { USE_MOCKS, mockDelay, apiGet, apiGetList, apiPost, apiPatch, apiDelete, apiFetchRaw } from './client';
import {
  kpisByBusiness, recentActivity, allBusinesses, currentBusiness,
  invoicesByBusiness, allInvoices, subscriptionsByBusiness,
  type Kpis, type Activity, type Business, type Invoice, type Subscription,
} from '../mocks/data';

export type DashboardData = { kpis: Kpis; activity: Activity[] };

export async function getDashboard(businessId: string): Promise<DashboardData> {
  if (!USE_MOCKS) return apiGet<DashboardData>(`/businesses/${businessId}/dashboard`);
  const kpis = kpisByBusiness[businessId] ?? kpisByBusiness[currentBusiness.id];
  if (!kpis) throw new Error(`no dashboard data for ${businessId}`);
  return mockDelay({ kpis, activity: recentActivity });
}

export async function listBusinesses(): Promise<Business[]> {
  if (!USE_MOCKS) return apiGetList<Business>('/businesses');
  return mockDelay(allBusinesses);
}

export async function listInvoices(businessId?: string): Promise<Invoice[]> {
  if (!USE_MOCKS) {
    return apiGetList<Invoice>(businessId ? `/businesses/${businessId}/invoices` : '/invoices');
  }
  if (!businessId) return mockDelay(allInvoices);
  return mockDelay(invoicesByBusiness[businessId] ?? []);
}

/** Full single-invoice payload — real lines + payments from GET /invoices/:id. */
export type InvoiceLineRow = {
  id: string;
  lineType: string;
  description: string;
  quantity: number;
  unitAmountMinor: number;
  amountMinor: number;
};
export type InvoicePaymentRow = {
  id: string;
  provider: string;
  status: string;
  amountMinor: number;
  paidAt: string | null;
};
export type InvoiceDetailPayload = {
  id: string;
  number: string;
  status: string;
  issuedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
  currencyCode: string;
  subtotalMinor: number;
  discountMinor: number;
  taxMinor: number;
  totalMinor: number;
  amountPaidMinor: number;
  lines: InvoiceLineRow[];
  payments: InvoicePaymentRow[];
  business: { id: string; name: string } | null;
};

export async function getInvoiceDetail(invoiceId: string): Promise<InvoiceDetailPayload> {
  if (!USE_MOCKS) return apiGet<InvoiceDetailPayload>(`/invoices/${invoiceId}`);
  const inv = allInvoices.find((i) => i.id === invoiceId) ?? allInvoices[0];
  if (!inv) throw new Error('invoice not found');
  const paid = inv.status === 'paid';
  return mockDelay({
    id: inv.id,
    number: inv.number,
    status: inv.status,
    issuedAt: inv.date,
    dueAt: inv.date,
    paidAt: paid ? inv.date : null,
    currencyCode: 'BDT',
    subtotalMinor: inv.amount,
    discountMinor: 0,
    taxMinor: 0,
    totalMinor: inv.amount,
    amountPaidMinor: paid ? inv.amount : 0,
    lines: [{ id: 'l1', lineType: 'subscription', description: 'Subscription', quantity: 1, unitAmountMinor: inv.amount, amountMinor: inv.amount }],
    payments: paid ? [{ id: 'p1', provider: 'sslcommerz', status: 'succeeded', amountMinor: inv.amount, paidAt: inv.date }] : [],
    business: null,
  });
}

export async function getSubscription(businessId: string): Promise<Subscription> {
  if (!USE_MOCKS) return apiGet<Subscription>(`/businesses/${businessId}/subscription`);
  const sub = subscriptionsByBusiness[businessId];
  if (!sub) throw new Error(`no subscription for ${businessId}`);
  return mockDelay(sub);
}

export async function getSubscriptionsMap(): Promise<Record<string, Subscription>> {
  if (!USE_MOCKS) return apiGet<Record<string, Subscription>>('/subscriptions');
  return mockDelay(subscriptionsByBusiness);
}

export type PlanLimits = { locations: number | null; users: number | null; products: number | null; invoices: number | null; invoicesYearly: number | null };
export type PlanOption = {
  priceId: string; packageId: string; name: string; unitAmountMinor: number; interval: 'month' | 'quarter' | 'year'; limits?: PlanLimits | null;
  /** Upgrades only: what it costs TODAY — the difference for the days left (server billing/proration.ts) — and when it renews. */
  dueToday?: { amountDueMinor: number; waived: boolean; renewsOn: string; rule: 'keep' | 'restart' } | null;
  /** Upgrades only: why it cannot be bought here (e.g. yearly → monthly happens at renewal). */
  blockedReason?: string | null;
};
export type AvailablePlans = {
  current: { packageId: string | null; priceId: string | null; unitAmountMinor: number; interval: string } | null;
  upgrades: PlanOption[];
  downgrades: PlanOption[];
};
export async function getAvailablePlans(businessId: string): Promise<AvailablePlans> {
  if (!USE_MOCKS) return apiGet<AvailablePlans>(`/businesses/${businessId}/subscription/plans`);
  // Mock: a monthly Pro customer 20 days from renewal — a same-interval upgrade keeps the date,
  // a yearly one restarts it (credit for the unused month).
  const limits = { locations: 5, users: 15, products: 10000, invoices: null, invoicesYearly: null };
  return mockDelay<AvailablePlans>({
    current: { packageId: 'pkg_pro', priceId: null, unitAmountMinor: 150000, interval: 'month' },
    upgrades: [
      { priceId: 'price_biz_m', packageId: 'pkg_biz', name: 'Business', unitAmountMinor: 250000, interval: 'month', limits,
        dueToday: { amountDueMinor: 66700, waived: false, renewsOn: '2026-10-25', rule: 'keep' }, blockedReason: null },
      { priceId: 'price_pro_y', packageId: 'pkg_pro', name: 'Pro', unitAmountMinor: 1500000, interval: 'year', limits,
        dueToday: { amountDueMinor: 1400000, waived: false, renewsOn: '2027-10-05', rule: 'restart' }, blockedReason: null },
    ],
    downgrades: [
      { priceId: 'price_start_m', packageId: 'pkg_start', name: 'Starter', unitAmountMinor: 80000, interval: 'month', limits },
    ],
  });
}

export type TeamMember = {
  id: string;
  name: string;
  email: string;
  username?: string | null;
  role: string;
  /** Stored POS-role label (fallback — the live truth comes from getPosUserRoles). */
  posRole?: string | null;
  status: string;
  joinedAt: string | null;
  /**
   * True for an OUTSTANDING INVITE rather than a real member. These rows were absent
   * from the list entirely even though an accepted invite consumes a plan user seat —
   * so an owner at their limit saw fewer people than seats used, with no way to find
   * or revoke the invisible one. `id` is the invite's public id, not a user's, so
   * member-scoped actions must not be offered on these rows.
   */
  pendingInvite?: boolean;
};

export async function getTeam(businessId: string): Promise<TeamMember[]> {
  if (!USE_MOCKS) return apiGetList<TeamMember>(`/businesses/${businessId}/team`);
  return mockDelay([]);
}

/** A business's REAL locations from its POS DB (drives the invite Locations picker). */
export type PosLocation = { id: number; name: string; locationId: string | null; isActive: boolean };
export async function getPosLocations(businessId: string): Promise<PosLocation[]> {
  if (USE_MOCKS) return mockDelay([]);
  const res = await apiGet<{ ok: boolean; locations: PosLocation[] }>(`/businesses/${businessId}/pos-locations`);
  return Array.isArray(res.locations) ? res.locations : [];
}

/** Live username/email → POS-role map from the business's POS DB (Users tab display). */
export type PosUserRole = { username: string | null; email: string | null; role: string | null; allowLogin: boolean };
export async function getPosUserRoles(businessId: string): Promise<PosUserRole[]> {
  if (USE_MOCKS) return mockDelay([]);
  const res = await apiGet<{ ok: boolean; users: PosUserRole[] }>(`/businesses/${businessId}/pos-user-roles`);
  return Array.isArray(res.users) ? res.users : [];
}

/** Edit a member (SaaS name / portal role / POS role) — platform + owning reseller. */
export const updateTeamMember = (businessId: string, userId: string, patch: { name?: string; role?: 'admin' | 'staff' | 'readonly'; posRole?: string }) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPatch<{ ok: boolean }>(`/businesses/${businessId}/team/${userId}`, patch);

/** Remove a member (their POS login is disabled too; account soft-deactivated when orphaned). */
/**
 * Transfer ownership to another EXISTING member (#3). The new owner must already be
 * a member — there is deliberately no invite-and-promote, so a business cannot be
 * handed to a stranger by a typo. The outgoing owner is demoted, not removed.
 */
export const transferBusinessOwnership = (businessId: string, userId: string) =>
  USE_MOCKS
    ? mockDelay({ ok: true, ownerEmail: '', previousOwnerEmail: null })
    : apiPost<{ ok: boolean; ownerEmail: string; previousOwnerEmail: string | null }>(
        `/businesses/${businessId}/transfer-ownership`,
        { userId },
      );

export const removeTeamMember = (businessId: string, userId: string) =>
  USE_MOCKS
    ? mockDelay({ ok: true, accountDeactivated: false, posLoginDisabled: true })
    : apiDelete<{ ok: boolean; accountDeactivated: boolean; posLoginDisabled: boolean }>(`/businesses/${businessId}/team/${userId}`);

export type AddOnsData = {
  active: Array<{ id: string; name: string; unitPriceMinor: number; qty: number; oneTime: boolean }>;
  available: Array<{ id: string; name: string; unitPriceMinor: number; description: string; oneTime: boolean }>;
};

export async function getAddOns(businessId: string): Promise<AddOnsData> {
  if (!USE_MOCKS) return apiGet<AddOnsData>(`/businesses/${businessId}/add-ons`);
  return mockDelay({ active: [], available: [] });
}

/** One of a staff member's businesses — see StaffContext.businesses. */
export type StaffContextBusiness = {
  /** publicId — passed to the POS SSO handoff (openPos). */
  businessId: string;
  businessName: string;
  /** POS subdomain handle; the host is `<slug>.<brand.domain>`. */
  slug: string;
  roleLabel: string;
  adminName: string | null;
};

/**
 * Staff (POS-only) landing context — see GET /staff/context. The top-level
 * fields describe the FIRST business (kept for backward compatibility); a
 * staff member on more than one business also gets `businesses`, one entry
 * per membership, so the landing page can offer a switcher.
 */
export type StaffContext = StaffContextBusiness & {
  staffName: string;
  businesses: StaffContextBusiness[];
};

export async function getStaffContext(): Promise<StaffContext> {
  if (!USE_MOCKS) return apiGet<StaffContext>('/staff/context');
  const business = {
    businessId: 'biz_khan',
    businessName: 'Khan Bakery',
    slug: 'khan-bakery',
    roleLabel: 'Cashier',
    adminName: 'Imran Khan',
  };
  return mockDelay({
    staffName: 'Rina Akter',
    ...business,
    businesses: [business],
  });
}

/* ── Full-database export (owner) ────────────────────────────────────────────────────
 * The owner may ask for a full copy of their OWN business's database. A platform admin
 * approves, which opens a 48-hour window. Only a business on its own dedicated database
 * has anything to export — one still sharing a schema with 60+ other businesses does not.
 * Wire shape is the same serializer the admin and reseller surfaces read.
 */
export type { DbExportRequest } from './admin';
import type { DbExportRequest } from './admin';

export const requestOwnDbExport = (businessId: string, reason: string) =>
  apiPost<DbExportRequest>(`/businesses/${businessId}/database/export-request`, { reason });

export const getOwnDbExport = (businessId: string) =>
  apiGet<{ current: DbExportRequest | null; history: DbExportRequest[] }>(`/businesses/${businessId}/database/export`);

export const useOwnDbExport = (businessId: string, enabled = true) =>
  useQuery({
    queryKey: ['owner', 'business', businessId, 'db-export'],
    queryFn: () => getOwnDbExport(businessId),
    enabled: !!businessId && enabled && !USE_MOCKS,
  });

/**
 * Fetch the approved dump and hand the browser a file. Same reasoning as the admin and
 * reseller copies: a Bearer-authenticated endpoint cannot be reached by a plain <a href>,
 * and a blob: URL means a download manager that re-requests the link sees only local data.
 */
export async function downloadOwnDatabase(businessId: string, dbName: string): Promise<void> {
  const res = await apiFetchRaw(`/businesses/${businessId}/database/download`);
  if (!res.ok) {
    let message = 'The download could not be started.';
    try { message = (await res.json())?.error?.message ?? message; } catch { /* non-JSON error body */ }
    throw new Error(message);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url; a.download = `${dbName}.sql.gz`;
    document.body.appendChild(a); a.click(); a.remove();
  } finally { URL.revokeObjectURL(url); }
}
